import { Modal, Radio } from 'antd';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IMusselLineResource } from '../../../entities/farms.entities';
import {
  loadFarmsData,
  loadQueueData,
  loadSpatStorages,
  saveFarmSeedingForm,
} from '../../../store/farms/farms.actions';
import { addUtilData } from '../../../store/utils/utils.actions';
import {
  parseImageUrlToFileItem,
  toMillisecond,
  toSecond,
  getAverageSize,
  numToStr,
  getUnitOfSpecie,
} from '../../../lib/common.helpers';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Feedback,
  Input,
  RadioButton,
  Subtitle,
} from '../../shared';
import TimeTrackInput, {
  toSecTimeTracks,
} from '../../shared/input/TimeTrackInput';
import FilesWall from '../../shared/pictures-wall/FilesWall';
import { ICustomValue } from '../../../entities/automation.entities';
import {
  selectProfile,
  selectAccount,
} from '../../../store/auth/auth.selector';
import { sendSingleRequest } from '../../../apis';
import { showFeedback } from '../../../store/ui/ui.actions';
import { IFileWall, ITimeRange } from '../../../entities/general.entities';
import { renderCustomError } from '.';
import { loadInventories } from '../../../store/inventories/inventories.actions';
import { selectLang } from '../../../store/ui/ui.selector';
import { translate, transUnit } from '../../../lib/lang.helper';
import {
  SelectInventoriesByFarmID,
  SelectIsBudgetVisible,
  SelectMusselSpatsByFarmID,
  SelectSeasonsByFarmID,
  SelectSeedingFields,
  SelectMusselFlupsySpatsByFarmID,
  SelectDetailedBudgetEnabled,
  SelectAccountSetting,
  SelectFarmsByFarmID,
} from '../../../store/extra/extra.selector';
import DirectionForm from '../../farm-util/DirectionForm';
import BatchSelection from '../../farm-util/BatchSelection';
import {
  extractSeedingForm,
  getSpatSourceLabel,
  inventoryLabel,
} from '../../../lib/farm.helpers';
import InstantMusselAssessment from './InstantMusselAssessment';
import { getStageLabel, StageLabelSpan } from '../../CommonViews';
import MusselSeedLengthForm from './MusselSeedLengthForm';
import OperatorBudgetForm from '../OperatorBudgetForm';
import MinMaxInputForm from './MinMaxInputForm';
import { IExpenseRequest } from '../../../entities/budget.entities';
import { initialBudgetForm } from '../../../lib/form.helpers';
import SeedSpatUpdateForm from './SeedSpatUpdateForm';
import ModalFeedbackView from '../../shared/feedback/ModalFeedbackView';
import CustomValuesForm from '../../farm-util/CustomValuesForm';
import SelectBoat from '../../farmform/SelectBoat';
import { TaggingManageForm } from '../../lines/LineTagButton';
import { loadTags } from '../../../store/automation/automation.actions';
import TaxonomyGroupsForm from '../../farmform/TaxonomyGroupsForm';

interface IQuantity {
  id: number;
  quantity: number;
}

interface IFormData {
  season_name: string;
  spat_storage_id: number | null;
  spat_amount: number | null;
  planned_date_seed: number;
  planned_date_harvest: number;
  line_length: number | null;
  fill_percent?: number;
  spat_size: number;
  spat_size_max: number | null;
  density: number;
  density_max: number | null;
  density_unit: 'spat per m' | 'kgs per m';
  drop?: number;
  submersion?: number;
  spacing?: number;
  comment: string | null;
  budget_unit_amount: number | null;
  budget_unit_price: number | null;
  image_pdf_files?: IFileWall[];
  time_tracks?: ITimeRange[];
  direction: string | null;
  boat_id?: number | null;
  parent_id?: number | null;
  picked_inventories?: IQuantity[] | null;
  custom_values?: ICustomValue[];
  stage: number | null;
  is_catch_spat?: boolean;

  cost?: number | null;
  expenses?: IExpenseRequest[];

  assessment_data?: any;

  tagged_ids?: number[] | null;
  taxonomy_group_ids: number[];
}

const defaultForm: IFormData = {
  season_name: '',
  spat_storage_id: null,
  spat_amount: null,
  planned_date_seed: moment().toDate().getTime(),
  planned_date_harvest: moment().toDate().getTime(),
  line_length: 0,
  spat_size: 0,
  spat_size_max: null,
  stage: 1,
  drop: undefined,
  spacing: undefined,
  submersion: undefined,
  density: 0,
  density_max: null,
  density_unit: 'spat per m',
  direction: null,
  boat_id: null,
  comment: null,
  budget_unit_amount: null,
  budget_unit_price: null,
  image_pdf_files: [],
  time_tracks: [],
  taxonomy_group_ids: [],
};

interface Props {
  visible: boolean;
  className?: string;
  title?: string;
  width?: number;
  lineData: IMusselLineResource;
  data?: any;
  updateID?: number;
  parentID?: number;
  onlyView?: boolean;
  onConfirm: (data: any) => void;
  onCancel: () => void;
  overConfirm?: (data: any) => Promise<any>;
  confirmBtnLabel?: string;
  lineChangeable?: boolean;
  warningMessage?: string | React.ReactNode;
}

const MusselSeedModal = ({
  visible,
  className,
  title,
  width,
  lineData,
  data,
  updateID,
  parentID,
  onlyView,
  onConfirm,
  onCancel,
  overConfirm,
  confirmBtnLabel,
  lineChangeable,
  warningMessage,
}: Props) => {
  const dispatch = useDispatch<any>();

  const lang = useSelector(selectLang);
  const spats = lineData.farm_is_flupsy
    ? SelectMusselFlupsySpatsByFarmID(lineData.farm_id)
    : SelectMusselSpatsByFarmID(lineData.farm_id);
  const seasonData = SelectSeasonsByFarmID(lineData.farm_id).map(x => ({
    id: x.id.toString(),
    label: x.name,
    value: x.name,
  }));
  const inventories = SelectInventoriesByFarmID(lineData.farm_id);
  const showBudget = SelectIsBudgetVisible(lineData.farm_id);
  const detailedBudgetEnabled = SelectDetailedBudgetEnabled(lineData.farm_id);
  const seedStages = useSelector(selectAccount)?.seed_stages_size;
  const farmsData = SelectFarmsByFarmID(lineData.farm_id);
  const customFields = SelectSeedingFields(lineData.farm_id);
  const defaultSavedForm = extractSeedingForm(
    farmsData.find(x => x.id === lineData.farm_id),
    data,
  );
  const curBoat = useSelector(selectProfile)?.current_boat;
  const accountSetting = SelectAccountSetting(lineData.farm_id);
  const stageLabels = accountSetting?.stage_labels;
  const defaultDropperLength = accountSetting?.default_dropper_length;
  const unitType = useSelector(selectAccount)?.display_unit;

  const getDensityUnitSpecie = (densityUnit: 'spat per m' | 'kgs per m') => {
    return densityUnit === 'kgs per m' ? 'kg_per_m' : 'spat_mer_m';
  };

  const [disabled, setDisabled] = useState(false);
  const [isSeasonNew, setIsSeasonNew] = useState<'old' | 'new'>('old');
  const [errors, setErrors] = useState<any>();
  const [formData, setFormData] = useState<IFormData>({
    ...defaultForm,
    drop:
      defaultDropperLength != null ? defaultDropperLength : defaultForm.drop,
    boat_id: data?.boat_id ?? curBoat?.id,
    assessment_data: data?.assessment_data ?? undefined,
    fill_percent: lineData.fill_percent ?? undefined,
    expenses: detailedBudgetEnabled
      ? [{ name: '', quantity: null, unit_price: null, amount: null }]
      : undefined,
    ...defaultSavedForm,
    custom_values: data?.custom_values ?? undefined,
    image_pdf_files: [],
    tagged_ids: data?.tagged_ids ?? null,
    is_catch_spat: data?.is_catch_spat ?? undefined,
  });
  const [stageMsg, setStageMsg] = useState<string>();
  const [selectedInventories, setSelectedInventories] = useState<number[]>(
    data?.picked_inventories
      ? data.picked_inventories.map((x: IQuantity) => x.id)
      : updateID
        ? []
        : inventories.some(x => x.id === lineData.inventory_id)
          ? [inventories.find(x => x.id === lineData.inventory_id)?.id]
          : [],
  );
  const [floatsQuantities, setFloatsQuantities] = useState<
    Record<number, number | undefined>
  >(
    data?.picked_inventories
      ? data.picked_inventories.reduce(
          (p: Record<number, number>, c: IQuantity) => {
            p[c.id] = c.quantity;
            return p;
          },
          {} as Record<number, number>,
        )
      : {},
  );
  const [visibleSpatUpdate, setVisibleSpatUpdate] = useState(false);

  const [curFarm, setCurFarm] = useState(
    farmsData.find(x => x.id === lineData.farm_id),
  );
  const [focLineId, setFocLineId] = useState(lineData.id);
  const [errorMsg, setErrorMsg] = useState<string>();
  const [dataProcessed, setDataProcessed] = useState<any>(null);

  const toggleDetailedAssess = (c: boolean) => {
    if (c) {
      setFormData(prv => ({
        ...prv,
        assessment_data: {
          size_min: prv.spat_size,
          size_max: prv.spat_size_max ?? prv.spat_size,
          size_avg: getAverageSize(prv.spat_size, prv.spat_size_max),
        },
      }));
    } else {
      setFormData(prv => ({
        ...prv,
        assessment_data: undefined,
      }));
    }
  };
  const updateForm = (key: keyof IFormData, val: any) => {
    if (
      [
        'spat_amount',
        'line_length',
        'fill_percent',
        'spat_size',
        'spat_size_max',
      ].includes(key)
    ) {
      val = val.length <= 0 ? null : Number(val);
    } else if (key === 'comment') {
      val = val.length <= 0 ? null : val;
    }
    if (key === 'spat_storage_id' || key === 'spat_amount') {
      setErrors((prv: any) => ({
        ...(prv ?? {}),
        spat_storage_id: null,
        spat_amount: null,
      }));
    } else {
      setErrors((prv: any) => ({ ...(prv ?? {}), [key]: null }));
    }
    if (key === 'spat_storage_id') {
      const spat = spats.find(x => x.id === Number(val));
      let tmp = { ...formData, spat_storage_id: Number(val) };
      if (spat?.harvest) {
        if (spat.harvest.shell_length) {
          tmp.spat_size = spat.harvest.shell_length as number;
          if (tmp.assessment_data) {
            tmp.assessment_data.size_min = tmp.spat_size;
            tmp.assessment_data.size_max = tmp.spat_size;
            tmp.assessment_data.size_avg = tmp.spat_size;
          }
        }
        if (spat.harvest.shell_length_max) {
          tmp.spat_size_max = spat.harvest.shell_length_max;
          if (tmp.assessment_data) {
            tmp.assessment_data.size_max = tmp.spat_size_max;
            tmp.assessment_data.size_avg = getAverageSize(
              tmp.spat_size,
              tmp.spat_size_max,
            );
          }
        }
        if (spat.harvest.amount) {
          tmp.spat_amount = spat.harvest.amount;
        }
        if (spat.harvest.line_length) {
          tmp.line_length = spat.harvest.line_length;
        }
      }
      if (spat) {
        tmp.stage = spat.stage;
      }
      setFormData(tmp);
    } else if (key === 'spat_size' || key === 'spat_size_max') {
      let tmp = { ...formData, [key]: val };
      if (tmp.spat_size !== null && tmp.assessment_data) {
        tmp.assessment_data.size_min = tmp.spat_size;
        tmp.assessment_data.size_max = tmp.spat_size_max ?? tmp.spat_size;
        tmp.assessment_data.size_avg = getAverageSize(
          tmp.spat_size,
          tmp.spat_size_max,
        );
      }
      setFormData(tmp);
    } else {
      setFormData({ ...formData, [key]: val });
    }
  };
  const updateInventoryQuantity = (i: number, val: string) => {
    let tmp = { ...floatsQuantities };
    tmp[i] = val.length <= 0 ? undefined : Number(val);
    setFloatsQuantities(tmp);
  };
  const assessFormIsValid = () => {
    if (!formData.assessment_data) return true;

    const assessData = formData.assessment_data;
    if (!assessData.size_min || !assessData.size_max || !assessData.size_avg) {
      setErrorMsg('Assessment size measuring can not be empty');
      return false;
    }
    if (Number(assessData.size_min) > Number(assessData.size_max)) {
      setErrorMsg('Assessment size measuring range is incorrect');
      return false;
    }
    if (assessData.assess_bottom) {
      if (
        !assessData.btm_size_avg ||
        !assessData.btm_size_max ||
        !assessData.btm_size_min
      ) {
        setErrorMsg('_bottom_size_measuring_empty');
        return false;
      }
      if (Number(assessData.btm_size_max) < Number(assessData.btm_size_min)) {
        setErrorMsg('_bottom_size_measuring_range');
        return false;
      }
    }
    return true;
  };
  const validFormData = () => {
    let tmpErr = { ...(errors ?? {}) };
    let res = true;
    if (!formData.season_name) {
      tmpErr.season_name = translate(lang, 'Add name');
      res = false;
    }
    if (Number(formData.line_length) === 0) {
      tmpErr.line_length = translate(lang, '_line_length_must_greater');
      res = false;
    }
    if (
      Number(formData.planned_date_seed) > Number(formData.planned_date_harvest)
    ) {
      tmpErr.planned_date_seed = translate(lang, '_invalid_seed_date');
      res = false;
    }
    if (
      data?.is_catch_spat !== true &&
      (!formData.spat_storage_id ||
        !formData.spat_amount ||
        formData.spat_amount < 0)
    ) {
      tmpErr.spat_amount = translate(lang, '_sel_spat_weight');
      res = false;
    }
    if (
      data?.is_catch_spat !== true &&
      formData.spat_amount &&
      availableWeight !== null &&
      availableWeight !== undefined &&
      formData.spat_amount > availableWeight &&
      !(
        updateID &&
        Math.abs((data?.spat_amount ?? 0) - formData.spat_amount) < 0.00001
      )
    ) {
      setVisibleSpatUpdate(true);

      tmpErr.spat_amount = translate(lang, '_max_spat_amount', availableWeight);
      res = false;
    }
    if (!updateID) {
      for (let ivtId of selectedInventories) {
        const ivt = inventories.find(x => x.id === ivtId);
        if (!ivt) {
          setErrorMsg('_inv_found_2');
          res = false;
          break;
        }
        const qty = floatsQuantities[ivtId] ?? undefined;
        if (isNaN(Number(qty)) || Number(qty) <= 0) {
          setErrorMsg(translate(lang, '_invalid_ivt_qty', ivt.name ?? ''));
          res = false;
          break;
        }
        if (Number(qty) > ivt.available_quantity) {
          setErrorMsg(
            translate(
              lang,
              '_seed_floats_over',
              qty,
              ivt.name,
              ivt.available_quantity,
            ),
          );
          res = false;
          break;
        }
      }
    }
    if (
      formData.density_max !== null &&
      formData.density_max < formData.density
    ) {
      tmpErr.density = translate(lang, '_invalid_density_range');
      res = false;
    }
    if (
      formData.spat_size_max !== null &&
      formData.spat_size_max < formData.spat_size
    ) {
      tmpErr.spat_size = translate(lang, '_invalid_spat_size_range');
      res = false;
    }
    if (
      formData.time_tracks &&
      toSecTimeTracks(formData.time_tracks) === false
    ) {
      setErrorMsg('_invalid_time_range');
      res = false;
    }
    setErrors(tmpErr);
    if (!res) return null;

    if (!assessFormIsValid()) return null;

    let validData = {
      ...formData,
      line_id: focLineId,
      planned_date_seed: toSecond(formData.planned_date_seed),
      planned_date_harvest: toSecond(formData.planned_date_harvest),
      expenses: formData.expenses?.filter(x => x.name && !!x.amount),
    };

    if (!!parentID) {
      validData.parent_id = parentID;
    } else if (!updateID) {
      const ms = curFarm?.lines.find(x => x.id === focLineId)?.growing_cycle
        ?.main_seed;
      if (ms) {
        if (!window.confirm(translate(lang, '_line_seed_make_sure')))
          return null;
        validData.parent_id = ms.id;
      }
    }
    if (selectedInventories.length > 0) {
      validData.picked_inventories = selectedInventories.map(x => ({
        id: x,
        quantity: floatsQuantities[x] ?? 0,
      }));
    }
    if (formData.time_tracks) {
      const tt = toSecTimeTracks(formData.time_tracks);
      if (tt) {
        validData.time_tracks = tt;
      } else {
        setErrorMsg('_invalid_time_range');
        return null;
      }
    }
    if (
      lineData.seedings.some(
        x => x.id !== updateID && x.season_name === formData.season_name,
      ) &&
      !(updateID && data?.season_name === formData.season_name) &&
      !window.confirm(translate(lang, '_seeding_season_confirm'))
    )
      return null;

    validData.custom_values = formData.custom_values?.filter(
      x => !!x.field_val,
    );
    if (!updateID && !validData.parent_id && lineData.growing_cycle) {
      if (!window.confirm(translate(lang, '_line_seed_make_sure'))) return null;
      validData.parent_id = lineData.growing_cycle.main_seed.id;
    }

    return validData;
  };
  const handleConfirmClick = async () => {
    if (onlyView) return;
    let validData = validFormData();
    if (!validData) return;

    setErrorMsg(undefined);
    setDisabled(true);
    if (!seasonData.some(x => x.value === validData?.season_name)) {
      await dispatch(addUtilData('season', validData.season_name));
    }
    if (!!overConfirm) {
      const ovc = await overConfirm(validData);
      if (ovc !== true) {
        setDisabled(false);
        if (ovc.errors) setErrors(ovc.errors);
        if (ovc.message) setErrorMsg(ovc.message);
      }
      return;
    }
    const res = updateID
      ? await sendSingleRequest(
          validData,
          'PUT',
          `api/farm/line/seeding/${updateID}`,
          true,
        )
      : await sendSingleRequest(
          validData,
          'POST',
          'api/farm/line/seeding',
          true,
        );
    if (res.status) {
      if (validData.tagged_ids) {
        dispatch(loadTags());
      }
      if (curFarm) {
        saveFarmSeedingForm(dispatch, curFarm.id, {
          season_name: validData.season_name,
          spacing: validData.spacing,
        });
      }
      if (validData.assessment_data) {
        const assessmentForm = {
          ...validData.assessment_data,
          assessment_date: toSecond(validData.planned_date_seed),
          planned_date_harvest: toSecond(validData.planned_date_harvest),
          seeding_id: lineData.growing_cycle?.main_seed.id,
          line_id: focLineId,
          custom_values: validData.assessment_data.custom_values?.filter(
            (x: any) => !!x.field_val,
          ),
        };
        const resAssessment = await sendSingleRequest(
          assessmentForm,
          'POST',
          'api/farm/line/assessment/assessments',
          true,
        );
        if (!resAssessment.status) {
          setDisabled(false);
          setErrorMsg(
            translate(lang, resAssessment.data?.message ?? 'Unknown error'),
          );
          dispatch(loadFarmsData());
          return;
        }
      }
      dispatch(loadFarmsData());
      dispatch(loadSpatStorages());
      dispatch(loadQueueData());
      dispatch(
        showFeedback({
          isMessage: true,
          message:
            res.data?.message ??
            (updateID ? '_updated_data_ok' : '_seed_data_submitted_ok'),
          type: 'success',
        }),
      );
      if (selectedInventories) {
        dispatch(loadInventories());
      }
      onConfirm(validData);
    } else {
      setDisabled(false);
      setErrorMsg(res.data?.message ?? 'Unknown error');
      if (res.data?.errors) setErrors(res.data.errors);
    }
  };

  const selectedBatch = spats.find(x => x.id === formData.spat_storage_id);
  const availableWeight = selectedBatch
    ? selectedBatch.available_weight + (updateID ? (data?.spat_amount ?? 0) : 0)
    : -1;

  const farmOptions = farmsData.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: `${x.name} - ${x.farm_number}`,
  }));
  const lineOptions = curFarm?.lines.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: x.line_name,
  }));

  useEffect(() => {
    if (
      updateID &&
      toSecond(formData.planned_date_seed) === toSecond(data.planned_date_seed)
    ) {
      return;
    }
    const spat = spats.find(x => x.id === Number(formData.spat_storage_id));
    if (!seedStages || seedStages.length <= 0 || !spat) {
      setStageMsg(undefined);
      return;
    }
    const i = formData.stage ?? spat.stage;
    if (i >= 0 && i < seedStages.length) {
      const gms = seedStages[i].time;
      const gsz = seedStages[i].size - (i === 0 ? 0 : seedStages[i - 1].size);
      const speed = gsz / gms;
      const leftSize = Math.max(
        0,
        seedStages[i].size - (formData.spat_size ?? 0),
      );
      const addTime = Math.ceil(leftSize / speed);
      setStageMsg(
        translate(
          lang,
          '_calculated_based_detected_stage',
          `${getStageLabel(stageLabels, i)} + ${addTime}`,
        ),
      );
      updateForm(
        'planned_date_harvest',
        moment(formData.planned_date_seed)
          .add(addTime, 'months')
          .toDate()
          .getTime(),
      );
    } else {
      setStageMsg(undefined);
    }
  }, [
    seedStages,
    formData.planned_date_seed,
    formData.stage,
    formData.spat_storage_id,
    formData.spat_size,
    stageLabels,
    updateID,
  ]);

  useEffect(() => {
    if (data && data !== dataProcessed) {
      let tmp: any = {};
      for (let k in formData) {
        tmp[k] = data[k] ?? (formData as any)[k];
        if (k === 'planned_date_seed' || k === 'planned_date_harvest') {
          tmp[k] = toMillisecond(tmp[k]);
        } else if (
          k === 'image_pdf_files' &&
          (tmp[k] === null || tmp[k] === undefined)
        ) {
          tmp[k] = [];
        }
      }
      if (data?.spat_storage && !tmp.spat_storage_id) {
        tmp.spat_storage_id = data.spat_storage.id;
        tmp.stage = data.spat_storage.stage;
      }
      if (data?.stage) {
        tmp.stage = data.stage;
      }
      if (tmp.time_tracks && tmp.time_tracks.length > 0) {
        tmp.time_tracks = tmp.time_tracks.map((x: any) => ({
          start_time: toMillisecond(x.start_time),
          finish_time: toMillisecond(x.finish_time),
        }));
      }
      if (showBudget) {
        tmp = { ...tmp, ...initialBudgetForm(detailedBudgetEnabled, data) };
      }
      if (tmp.image_pdf_files) {
        tmp.image_pdf_files = tmp.image_pdf_files.map((x: any) =>
          typeof x === 'object' && x !== null ? x : parseImageUrlToFileItem(x),
        );
      }

      tmp.taxonomy_group_ids =
        data.taxonomy_group_ids && data.taxonomy_group_ids.length > 0
          ? data.taxonomy_group_ids
          : (data.taxonomy_groups ?? []).map((x: { id: number }) => x.id);
      setFormData(tmp);
      setDataProcessed(data);
    }
  }, [data, showBudget, detailedBudgetEnabled]);

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      className={className}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={width !== undefined ? width : detailedBudgetEnabled ? 800 : 600}
    >
      {visibleSpatUpdate && formData.spat_storage_id && (
        <SeedSpatUpdateForm
          type='MUSSEL'
          farmID={lineData.farm_id}
          spatStorageID={formData.spat_storage_id}
          spatAmount={formData.spat_amount ?? 0}
          onClose={() => {
            setErrors(undefined);
            setVisibleSpatUpdate(false);
          }}
        />
      )}
      <div
        className='wrap'
        style={{ display: visibleSpatUpdate ? 'none' : undefined }}
      >
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, title ?? 'Mussel seed line')}
          </Subtitle>
        </div>
        <div>
          {!!warningMessage && (
            <div className='header-alert'>{warningMessage}</div>
          )}
          {!!errorMsg && (
            <Feedback
              message={translate(lang, errorMsg)}
              type={'error'}
              theme='light'
              position={10}
              isGlobal
              onClose={() => setErrorMsg(undefined)}
            />
          )}
          {!parentID && !!lineChangeable && (
            <div className='mb-17 d-flex'>
              <div className='w-100 mr-7'>
                <Dropdown
                  label={translate(lang, 'Select farm')}
                  options={farmOptions}
                  value={curFarm?.id?.toString()}
                  onChange={v =>
                    setCurFarm(farmsData.find(x => x.id === Number(v)))
                  }
                />
              </div>
              <div className='w-100 ml-7'>
                <Dropdown
                  label={translate(lang, 'Select line')}
                  options={lineOptions ?? []}
                  value={
                    !!lineOptions &&
                    lineOptions.some(x => Number(x.id) === focLineId)
                      ? focLineId.toString()
                      : undefined
                  }
                  onChange={v => setFocLineId(Number(v))}
                />
              </div>
            </div>
          )}
          <TaxonomyGroupsForm
            className='mb-17 mt-17'
            value={formData.taxonomy_group_ids}
            onChange={ids => updateForm('taxonomy_group_ids', ids)}
            disabled={onlyView}
          />
          <Radio.Group
            className='d-flex mt-14 mb-17'
            onChange={e => setIsSeasonNew(e.target.value)}
            value={isSeasonNew}
            disabled={onlyView}
          >
            <RadioButton
              label={translate(lang, 'Existing season')}
              value='old'
              disabled={onlyView}
            />
            <RadioButton
              className='ml-34'
              label={translate(lang, 'New season')}
              value='new'
              disabled={onlyView}
            />
          </Radio.Group>
          <div
            className={errors?.season_name ? 'invalid-form mb-16' : ' mb-16'}
          >
            {isSeasonNew === 'old' ? (
              <Dropdown
                label={translate(lang, 'Season name')}
                placeholder={translate(lang, 'Choose Season')}
                className='mr-16 w-100'
                onChange={value => updateForm('season_name', value)}
                options={seasonData}
                value={formData.season_name}
                disabled={onlyView}
              />
            ) : (
              <Input
                label={translate(lang, 'New Season')}
                placeholder={translate(lang, 'Season name')}
                type='text'
                value={formData.season_name}
                onChange={e => updateForm('season_name', e.target.value)}
                disabled={onlyView}
              />
            )}
            {!!errors?.season_name && (
              <div className='invalid-feedback'>{errors?.season_name}</div>
            )}
          </div>
          {!data?.is_catch_spat ? (
            <>
              <div
                className={
                  errors?.spat_amount || errors?.spat_storage_id
                    ? 'invalid-form mt-17'
                    : ' mt-17'
                }
              >
                <BatchSelection
                  className={`mb-${
                    formData.spat_storage_id === null ? '16' : '5'
                  }`}
                  batches={spats}
                  value={formData.spat_storage_id}
                  onChange={value => updateForm('spat_storage_id', value)}
                  disabled={onlyView}
                />
                {!!formData.spat_storage_id && (
                  <div className='mb-16'>
                    <Input
                      label={translate(lang, 'Amount')}
                      placeholder={
                        (availableWeight ?? 0) <= 0
                          ? translate(lang, 'Amount')
                          : translate(lang, '_max_spat_amount', availableWeight)
                      }
                      type='number'
                      value={numToStr(formData.spat_amount)}
                      max={
                        availableWeight !== null &&
                        availableWeight !== undefined &&
                        availableWeight > 0
                          ? availableWeight
                          : undefined
                      }
                      unit={getUnitOfSpecie(unitType, 'weight_kg')}
                      onChange={e => updateForm('spat_amount', e.target.value)}
                      disabled={onlyView}
                    />
                    {formData.spat_amount !== availableWeight &&
                      availableWeight !== null &&
                      availableWeight !== undefined && (
                        <div
                          className='color-blue-0 mt-4 ml-4 cursor-pointer'
                          onClick={() =>
                            updateForm(
                              'spat_amount',
                              availableWeight.toString(),
                            )
                          }
                        >
                          {translate(
                            lang,
                            '_full_spats_seeded',
                            getSpatSourceLabel(formData.spat_storage_id, spats),
                          )}
                        </div>
                      )}
                    {!!errors?.spat_amount && (
                      <div className='invalid-feedback'>
                        {renderCustomError(errors?.spat_amount)}
                      </div>
                    )}
                  </div>
                )}
                {!!selectedBatch && (
                  <div className='mb-16'>
                    <CheckboxButton
                      label={translate(
                        lang,
                        'Keep previous stage for this seeding',
                      )}
                      checked={formData.stage === selectedBatch.stage - 1}
                      disabled={onlyView || selectedBatch.stage <= 1}
                      onChange={e =>
                        updateForm(
                          'stage',
                          e.target.checked
                            ? selectedBatch.stage - 1
                            : selectedBatch.stage,
                        )
                      }
                    />
                  </div>
                )}
                <Dropdown
                  className='mt-7 mb-16'
                  label={translate(lang, 'Batch Stage')}
                  options={[1, 2, 3, 4].map(x => ({
                    id: x.toString(),
                    value: x.toString(),
                    label: (
                      <StageLabelSpan stage={x} farmId={lineData.farm_id} />
                    ),
                  }))}
                  value={formData.stage?.toString()}
                  onChange={v => updateForm('stage', Number(v))}
                />
                {!!errors?.spat_storage_id && (
                  <div className='invalid-feedback'>
                    {errors?.spat_storage_id}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className='pt-8 pb-16 pl-4'>
              <Radio.Group
                value={formData.is_catch_spat}
                onChange={e => updateForm('is_catch_spat', e.target.value)}
              >
                <Radio value={true}>
                  {translate(lang, '_line_is_catch_spat')}
                </Radio>
                <Radio value={false} className='mt-8'>
                  {translate(lang, '_line_was_catch_spat')}
                </Radio>
              </Radio.Group>
            </div>
          )}
        </div>
        <div
          className={`mb-24 mt-16 ${
            errors?.planned_date_seed ? 'invalid-form' : ''
          }`}
        >
          <Datepicker
            label={transUnit(lang, 'line_seed_date', curFarm?.structure)}
            defaultValue={formData.planned_date_seed}
            onChange={e =>
              updateForm(
                'planned_date_seed',
                e?.toDate().getTime() ?? moment().toDate().getTime(),
              )
            }
            required
            disabled={onlyView}
          />
          {errors?.planned_date_seed && (
            <div className='invalid-feedback'>{errors?.planned_date_seed}</div>
          )}
        </div>
        <div className='mb-16'>
          <Datepicker
            label={translate(lang, 'Planned harvest date')}
            defaultValue={formData.planned_date_harvest}
            onChange={e => {
              if (
                formData.planned_date_seed !== undefined &&
                e &&
                e!.toDate().getTime() < formData.planned_date_seed
              ) {
                updateForm('planned_date_harvest', moment().toDate().getTime());
              } else {
                updateForm(
                  'planned_date_harvest',
                  e?.toDate().getTime() ?? moment().toDate().getTime(),
                );
              }
            }}
            required
            disabled={onlyView}
          />
          {stageMsg && (
            <div className='color-red mt-7'>{translate(lang, stageMsg)}</div>
          )}
        </div>
        <MusselSeedLengthForm
          lineData={lineData}
          form={formData}
          onChange={setFormData}
          errors={errors}
          setErrors={setErrors}
          disabled={onlyView}
          unitType={unitType}
        />
        <div className='mb-16'>
          <Input
            label={transUnit(lang, 'fill_percent', curFarm?.structure)}
            type='number'
            value={numToStr(formData.fill_percent)}
            unit='%'
            onChange={e => updateForm('fill_percent', e.target.value)}
            disabled={onlyView}
          />
        </div>
        {lineData.line_type !== 'DROPPERS' && (
          <div className='mb-16'>
            <DirectionForm
              value={formData.direction}
              onChange={v => updateForm('direction', v)}
            />
          </div>
        )}
        <SelectBoat
          className='mb-24'
          value={formData.boat_id}
          onChange={v => updateForm('boat_id', v)}
        />
        {!!formData.time_tracks && (
          <TimeTrackInput
            data={formData.time_tracks}
            onChange={t => updateForm('time_tracks', t)}
            baseDate={formData.planned_date_seed}
            disabled={onlyView}
          />
        )}
        <MinMaxInputForm
          className='mt-12'
          label={translate(lang, 'Spat Size')}
          min={formData.spat_size}
          max={formData.spat_size_max}
          onMinChange={v => updateForm('spat_size', v)}
          onMaxChange={v => updateForm('spat_size_max', v)}
          unit={getUnitOfSpecie(unitType, 'size_mm')}
          errorMsg={errors?.spat_size}
        />
        <MinMaxInputForm
          className='mt-12'
          label={translate(lang, 'Density')}
          min={formData.density}
          max={formData.density_max}
          onMinChange={v => updateForm('density', v)}
          onMaxChange={v => updateForm('density_max', v)}
          unit={getUnitOfSpecie(
            unitType,
            getDensityUnitSpecie(formData.density_unit),
          )}
          errorMsg={errors?.density}
          disabled={onlyView}
          additionalElement={
            <>
              <Radio.Group
                className='d-flex align-center'
                onChange={e => updateForm('density_unit', e.target.value)}
                value={formData.density_unit}
              >
                <RadioButton
                  label={getUnitOfSpecie(unitType, 'spat_mer_m')}
                  value='spat per m'
                />
                <RadioButton
                  className='ml-17'
                  label={getUnitOfSpecie(unitType, 'kg_per_m')}
                  value='kgs per m'
                />
              </Radio.Group>
            </>
          }
        />
        {!updateID && (
          <div className='pb-17'>
            <Dropdown
              label={translate(lang, 'Select inventory')}
              placeholder={translate(lang, 'Select inventory')}
              mode='multiple'
              options={inventories.map(x => ({
                id: x.id.toString(),
                value: x.id.toString(),
                label: inventoryLabel(x),
              }))}
              value={selectedInventories.map(String) as any}
              onChange={(values: any) =>
                setSelectedInventories(values.map(Number))
              }
              disabled={onlyView}
            />
          </div>
        )}
        {selectedInventories.length > 0 && (
          <div className='mb-17'>
            {selectedInventories.map(ivtId => (
              <Input
                className='mb-15'
                key={ivtId}
                type='number'
                label={inventoryLabel(inventories.find(t => t.id === ivtId))}
                placeholder={translate(
                  lang,
                  'Maximum value is %s',
                  inventories.find(t => t.id === ivtId)?.available_quantity ??
                    '',
                )}
                value={numToStr(floatsQuantities[ivtId])}
                onChange={e => updateInventoryQuantity(ivtId, e.target.value)}
                max={inventories.find(t => t.id === ivtId)?.available_quantity}
                disabled={onlyView}
              />
            ))}
          </div>
        )}
        {!!showBudget && (
          <OperatorBudgetForm
            detailedBudgetEnabled={detailedBudgetEnabled}
            form={formData}
            onChange={setFormData}
            disabled={onlyView}
            showUnits={true}
          />
        )}
        <CustomValuesForm
          fields={customFields}
          values={formData.custom_values ?? []}
          onChange={d => setFormData(prv => ({ ...prv, custom_values: d }))}
          disabled={onlyView}
        />
        <div className='mt-17 mb-17'>
          <Input
            label={translate(lang, 'Comment')}
            placeholder={translate(lang, 'Comment')}
            type='textarea'
            value={formData.comment ?? ''}
            onChange={e => updateForm('comment', e.target.value)}
            disabled={onlyView}
          />
        </div>
        {!!formData.image_pdf_files && (
          <div className='mb-17'>
            <FilesWall
              label={translate(lang, 'Images & PDF Files')}
              fileList={formData.image_pdf_files}
              onChange={v => updateForm('image_pdf_files', v)}
              disabled={onlyView}
            />
          </div>
        )}
        <div className='mb-17'>
          <CheckboxButton
            label={translate(lang, '_create_detailed_assess')}
            checked={!!formData.assessment_data}
            onChange={e => toggleDetailedAssess(e.target.checked)}
          />
        </div>
        {!!formData.assessment_data && (
          <InstantMusselAssessment
            lineData={lineData}
            onChange={data =>
              setFormData(prv => ({ ...prv, assessment_data: data }))
            }
            formData={formData.assessment_data}
            disabled={disabled || onlyView}
          />
        )}
        {!updateID && (
          <TaggingManageForm
            className='mb-17 mt-17'
            lineData={lineData}
            tagIDs={formData.tagged_ids}
            onChange={ids => updateForm('tagged_ids', ids)}
          />
        )}
      </div>
      <div
        className='modal-button d-flex justify-end align-center'
        style={{ display: visibleSpatUpdate ? 'none' : undefined }}
      >
        <Button
          width={'small'}
          size={2}
          type='fill'
          color='red'
          className='rsp-btn ml-16'
          onClick={onCancel}
          disabled={disabled}
        >
          {translate(lang, 'Close')}
        </Button>
        <Button
          width={'small'}
          size={2}
          type='fill'
          color='green'
          className='rsp-btn ml-16'
          onClick={handleConfirmClick}
          disabled={disabled || onlyView}
        >
          {translate(lang, confirmBtnLabel ?? 'Confirm')}
        </Button>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default MusselSeedModal;
